import React from "react";
import { ArrowRightLeft, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { postIdOfWork } from "@/lib/workKind";
import { markdownToPlain } from "@/lib/markdownPlain";

const OVERDUE_CAP = 5;
const HANDOFF_CAP = 5;

function cardTitle(card) {
  return markdownToPlain(card?.title || "").trim() || "Untitled";
}

function handoffTitle(h) {
  const count = Number(h?.item_count ?? h?.itemCount ?? 1) || 1;
  const title =
    markdownToPlain(h?.post?.title || "").trim() ||
    `Post #${h?.postId ?? h?.post_id ?? ""}`;
  if (count <= 1) return title;
  return `${title} · ${count} items`;
}

function fromTeamName(h) {
  return (
    h?.fromTeam?.name ||
    h?.from_team?.name ||
    `Team ${h?.fromTeamId ?? h?.from_team_id ?? ""}`
  );
}

/**
 * Compact overdue + pending inbound handoff strip for team Feed.
 * Hidden when both lists are empty.
 */
export default function TeamStuckStrip({
  overdueCards = [],
  inboundHandoffs = [],
  kanbanEnabled = false,
  onOpenKanbanCard,
  onOpenHandoffs,
  onOpenKanban,
}) {
  const overdue = kanbanEnabled ? overdueCards.filter(Boolean) : [];
  const handoffs = Array.isArray(inboundHandoffs) ? inboundHandoffs : [];
  if (!overdue.length && !handoffs.length) return null;

  const overdueShown = overdue.slice(0, OVERDUE_CAP);
  const overdueMore = Math.max(0, overdue.length - overdueShown.length);
  const handoffShown = handoffs.slice(0, HANDOFF_CAP);
  const handoffMore = Math.max(0, handoffs.length - handoffShown.length);

  return (
    <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">What's stuck</h3>
        {kanbanEnabled && onOpenKanban ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={onOpenKanban}
          >
            Open board
          </Button>
        ) : null}
      </div>
      {overdueShown.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" />
            {overdue.length} overdue
          </p>
          <ul className="space-y-0.5">
            {overdueShown.map((card) => {
              const id = postIdOfWork(card);
              return (
                <li key={id || cardTitle(card)}>
                  <button
                    type="button"
                    className="w-full text-left text-sm truncate hover:underline"
                    onClick={() => onOpenKanbanCard?.(card)}
                  >
                    {cardTitle(card)}
                  </button>
                </li>
              );
            })}
          </ul>
          {overdueMore > 0 && onOpenKanban ? (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:underline"
              onClick={onOpenKanban}
            >
              {overdueMore} more on the board
            </button>
          ) : null}
        </div>
      ) : null}
      {handoffShown.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <ArrowRightLeft className="h-3.5 w-3.5" />
            {handoffs.length} pending handoff{handoffs.length === 1 ? "" : "s"}
          </p>
          <ul className="space-y-0.5">
            {handoffShown.map((h) => {
              const hid = h?.handoff_id ?? h?.handoffId;
              return (
                <li key={hid || handoffTitle(h)}>
                  <button
                    type="button"
                    className="w-full text-left text-sm truncate hover:underline"
                    onClick={() => onOpenHandoffs?.()}
                  >
                    {handoffTitle(h)}
                    <span className="text-muted-foreground">
                      {" "}
                      · from {fromTeamName(h)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {handoffMore > 0 ? (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => onOpenHandoffs?.()}
            >
              {handoffMore} more
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
